import { Menu } from 'lucide-react'
import { Suspense, useState } from 'react'
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { IconPortfolio } from '@/ui/icons'
import { Skeleton } from '@/ui/primitives'
import { Button } from '@/ui/Button'
import { ConnectButton } from './ConnectButton'
import { Sidebar, SidebarDrawer } from './Sidebar'
import { ThemeToggle } from './theme'
import { AppFooter } from './AppFooter'
import { useWalletGate } from '@/lib/wallet-gate'
import { canAccessRoute } from '@/lib/referral-access'
import { signInFailureReason } from '@/hooks/useAuth'
import { GoogleTranslate } from './GoogleTranslate'

/**
 * App shell — fixed left rail, no header bar.
 *
 * ── What changed and why ──
 * This was a two-tier header: navigation across the top, live vitals beneath it.
 * That works, but it spent 104px of vertical space on every route to hold six links
 * and six numbers, and vertical space is the scarce axis on a trading screen —
 * horizontal space, at these widths, is not.
 *
 * A rail inverts that trade: it costs 280px of width the layout has to spare, gives
 * the vitals a permanent home that does not push content down, and leaves the top of
 * every page for the page's own title. The only chrome left above the content is the
 * wallet control, which has to be reachable from everywhere and belongs nowhere else.
 *
 * The rail is fixed rather than scrolling with the page: navigation that scrolls out
 * of reach is navigation you have to hunt for.
 */

/**
 * Routes that opt out of the centred content container.
 *
 * The perpetuals terminal is a fixed-height resizable grid — chart, order book,
 * trade ticket and positions all competing for space. Inside a padded, max-width
 * wrapper it loses enough room that the order book cannot render rows at all.
 */
const FULL_BLEED_ROUTES = ['/trade']

export function AppShell() {
  const location = useLocation()
  const { hasReferralAccess, isSignedIn, needsReferral } = useWalletGate()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const fullBleed = FULL_BLEED_ROUTES.some((r) => location.pathname.startsWith(r))
  return (
    /*
     * ── No `data-theme` here, and NO `bg-canvas` ──
     *
     * The per-route dark pin that used to sit on this element is gone. Its list had been
     * empty for months, and an override BELOW <html> would quietly beat the user's explicit
     * theme choice on whichever routes it named — a toggle that does nothing on some pages
     * and no clue why. The theme lives on <html> alone now, set by the pre-paint script in
     * index.html and by layout/theme.tsx.
     *
     * The ground is left unpainted for a separate reason, and it is easy to put back by
     * accident. This div is full-viewport, so an opaque background on it paints straight over
     * the field <body> composites (see --bg-halftone in theme.css) — the layers keep being
     * drawn and immediately covered, which looks exactly like "the texture is too subtle" and
     * cannot be fixed by raising their opacity. <body> paints --bg-hall and <html> paints
     * --color-canvas beneath it; this element only needs to be transparent.
     */
    <div className="min-h-dvh text-ink">
      {/*
       * ── Fixed rail (desktop) ──
       *
       * The landing page's capital-line pipe used to run down the first 40px, which pushed the
       * rail to left-10 and the content to 304. It has been removed: on a page this dense it read
       * as a decorative stripe rather than an instrument, and it was spending 40px of every
       * viewport to restate a figure the PROTOCOL readout already gives in words.
       */}
      {/*
       * ── The rail paints NO background ──
       *
       * It was `surface-glass`, the card colour, which made the rail a panel sitting beside
       * the page rather than part of it. The reference runs one continuous ground under its
       * rail, its bar and its content and separates the rail with a single hairline — that is
       * what makes the whole window read as one surface.
       *
       * Transparent rather than `bg-canvas`: the body's texture is a FIXED layer, so leaving
       * the rail unpainted lets the grain run continuously beneath it. Painting it canvas
       * would match the colour but cut the grain off at the rail's edge, which is the kind of
       * seam you see without being able to name. Safe because content never scrolls under the
       * rail — the column is offset by its width.
       */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[var(--shell-rail-w)] border-r border-line lg:block">
        <Sidebar />
      </aside>

      {/* ── Drawer (mobile) ── */}
      <SidebarDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="flex min-h-dvh flex-col lg:pl-[var(--shell-rail-w)]">
        {/*
         * ── The app bar ──
         *
         * Account controls only. There is NO navigation in here.
         *
         * ── Why the section tabs were removed ──
         * The bar used to carry the current section's pages as tabs — Lend · Borrow · Multiply
         * · Lite · Markets. Every one of those already exists as a tile in the rail, so each
         * destination had two controls, and on /lend two enamel pills lit at once: one in the
         * rail, one eight pixels below it in the bar. Two active states for one location.
         *
         * The rail wins that contest because it is complete. It lists every destination in the
         * app, so removing the tabs costs no reachability — the tabs were a subset of it, not
         * a second level. (A previous attempt inverted this instead: rail keeps sections, bar
         * keeps pages. It also removes the duplication, but it hides eight destinations behind
         * a section click for no gain when the rail has room for all eleven.)
         *
         * This also matches the reference. defi.pots.money has exactly one navigation surface
         * — its rail — and a top bar holding nothing but Portfolio, Connect, theme and
         * language. Account controls right, navigation left, no overlap.
         *
         * ── Why it keeps the border and the blur, with only a button in it ──
         * An earlier version of this strip had no edge on purpose, so a lone button would
         * float rather than sit in a band. But content scrolls UNDER a sticky bar, and without
         * a surface the wallet control ends up overlapping figures as you scroll. The hairline
         * is what makes it read as chrome the page passes behind.
         *
         * ── Why the blur band is full-bleed and the CONTENT is centred ──
         * The band used to be `mx-auto max-w-7xl` itself, so on a wide monitor the translucent
         * blur simply stopped at 1280px, leaving a hard vertical seam down each side and a
         * floating rectangle instead of a bar. The band now runs edge to edge and the inner
         * row carries the max-width, so the bar spans the viewport while staying aligned with
         * the content beneath it.
         *
         * `bar/75`, not `canvas/85`. The hall gradient runs light at the top and dark at the
         * bottom, so a translucent `canvas` band painted across the top of the page came out
         * DARKER than the page behind it — a visible stripe rather than a blur.
         */}
        <div className="sticky top-0 z-20 bg-canvas/60 backdrop-blur-md">
          <div
            className={cn(
              'flex h-[var(--shell-bar-h)] w-full max-w-full items-center gap-1 px-2 sm:gap-3 sm:px-6',
              !fullBleed && 'mx-auto max-w-7xl',
            )}
          >
            {/* Below lg the rail is a drawer, so this is the only way to reach navigation. */}
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="shrink-0 rounded-md p-2 text-muted transition-colors hover:bg-elevated hover:text-ink lg:hidden"
            >
              <Menu className="size-5" />
            </button>

            {/*
             * ── Portfolio, and why it is HERE and not in the rail ──
             *
             * The rail lists protocol areas — places that exist whether or not you are
             * connected. Portfolio is the opposite: it is empty without a wallet and it is
             * about you rather than about the protocol. Putting it in the rail would sit
             * "your holdings" beside "Swap" as though they were the same kind of thing, and
             * would also put a destination back in two menus, which is what we just removed.
             *
             * Beside the wallet control it reads correctly as an account control — which is
             * exactly where the reference puts its own Portfolio, out of its menu entirely.
             *
             * It stays visible while disconnected rather than appearing on connect: a control
             * that materialises after an action is a control nobody knows exists beforehand,
             * and the page has a real connect prompt of its own.
             */}
            {/*
             * `min-w-0`, NOT `shrink-0`.
             *
             * This group was shrink-0, which is a promise the layout cannot keep on a phone:
             * the network badge, the address chip, sign-out and the theme toggle together
             * exceed 390px, and a flex child that refuses to shrink pushes the PAGE wider than
             * the viewport instead. Two symptoms follow and they look unrelated — the content
             * sits left with a gap on the right, and the sticky bar stops short of the right
             * edge, because a sticky element is sized to the viewport while the body has become
             * wider than it. Letting the group shrink removes both at once.
             */}
            <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-3">
              {hasReferralAccess && <NavLink
                to="/portfolio"
                className={({ isActive }) =>
                  cn(
                    'hidden h-10 select-none items-center gap-2 rounded-[12px] px-4 text-sm font-semibold sm:inline-flex',
                    'transition-[box-shadow,background,color] duration-150',
                    isActive
                      ? 'bg-accent-wash text-accent [box-shadow:inset_0_0_0_1px_var(--color-accent-edge)]'
                      : 'text-ink [background:var(--btn-ghost-bg)] [box-shadow:var(--btn-ghost-shadow)] hover:[background:var(--btn-ghost-bg-hi)]',
                  )
                }
              >
                <IconPortfolio className="size-4" />
                <span>Portfolio</span>
              </NavLink>}

              <ConnectButton />

              <GoogleTranslate />

              <ThemeToggle />
            </div>
          </div>
        </div>

        <main className={cn('flex-1', fullBleed ? 'pb-0' : 'pb-10')}>
          {/* `pt-7` keeps the page header off the bar's hairline. */}
          <div className={cn(fullBleed ? '' : 'mx-auto max-w-7xl px-4 pb-2 pt-7 sm:px-6')}>
            {/*
             * Page entrance is a CSS animation (`.page-enter`) — see the note on that
             * class in theme.css. Two Framer-driven versions both shipped a blank-page
             * bug; the CSS one animates transform only, so a frozen timeline (a
             * background tab) can never leave content invisible.
             */}
            <div key={location.pathname} className="page-enter">
              <Suspense fallback={<PageFallback />}>
                {canAccessRoute(location.pathname, { isSignedIn, needsReferral }) ? (
                  <Outlet />
                ) : !isSignedIn ? (
                  <SignInRequired />
                ) : (
                  <Navigate to="/" replace />
                )}
              </Suspense>
            </div>
          </div>
        </main>

        {/* Outside the max-width wrapper so the hairline runs the full width of the content
            column, the way the bar's band does at the top. */}
        {!fullBleed && <AppFooter />}
      </div>
    </div>
  )
}

/**
 * `PageHeader` used to live here as a numbered station plate. It now lives in ui/page.tsx as
 * the icon-and-title header every page shares — see the note there for why the `ST-nn`
 * sequence was retired. Re-exported so the pages' import path does not have to churn.
 */
export { PageHeader } from '@/ui/page'

/** Shown while a lazy route chunk loads. Boxes match the real layout, so nothing shifts. */
function PageFallback() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

/**
 * Rendered in-place for a logged-out visit to any portal route. Keeping the attempted
 * URL means the normal auth state update reveals that exact page after SIWE succeeds.
 */
function SignInRequired() {
  const { state, actionLabel, runAction, signIn, signingIn, signInError } = useWalletGate()
  const walletReady = state === 'ready'

  return (
    <section className="surface-glass mx-auto max-w-lg rounded-[18px] p-7 text-center ring-1 ring-line">
      <p className="label">Ninja DEX portal</p>
      <h1 className="display mt-2 text-2xl text-ink">Sign in required</h1>
      <p className="mt-2 text-sm text-muted">To access this portal, please sign in.</p>
      <div className="mx-auto mt-6 max-w-xs">
        {walletReady ? (
          <Button type="button" fullWidth size="lg" loading={signingIn} onClick={signIn}>
            {signingIn ? 'Signing in…' : 'Sign in with wallet'}
          </Button>
        ) : (
          <Button type="button" fullWidth size="lg" disabled={state === 'reconnecting'} onClick={runAction}>
            {actionLabel ?? 'Connect wallet'}
          </Button>
        )}
      </div>
      {signInError && (
        <p role="alert" className="mt-3 text-xs text-danger">
          {signInFailureReason(signInError)}
        </p>
      )}
    </section>
  )
}
